import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Universal "Not Tax Advice" disclaimer modal. Shows once per user on first
 * login after registration. Persists acceptance via /api/auth/accept-disclaimer.
 */
export default function DisclaimerModal() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!user) return;
    const alreadyAccepted = user?.disclaimer_accepted_at;
    if (!alreadyAccepted) setOpen(true);
  }, [user]);

  const handleAccept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/accept-disclaimer`, {}, { withCredentials: true });
      if (refreshUser) await refreshUser();
      setOpen(false);
    } catch {
      // soft-fail — close anyway so the user isn't blocked
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      data-testid="disclaimer-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-5 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-extrabold text-amber-900" style={{ fontFamily: "Manrope" }}>
              Important — Not Tax Advice
            </h2>
            <p className="text-xs text-amber-800 mt-1">Please read and accept before continuing.</p>
          </div>
        </div>
        <div className="px-6 py-5 overflow-y-auto text-sm text-slate-700 leading-relaxed space-y-3">
          <p>
            The calculations and information shown in <strong>Today's Kidz Simple Sign-In</strong> are for
            <strong> organizational and bookkeeping purposes only.</strong> They are not a substitute for advice
            from a licensed CPA or tax professional.
          </p>
          <p>
            Tax law is complex and changes frequently. Always have your final return reviewed by a qualified
            professional before filing. <strong>Today's Kidz Simple Sign-In, its owners, and operators assume
            no liability</strong> for any tax decisions made using this software.
          </p>
          <p>
            By using this application, you acknowledge that <strong>you alone are responsible</strong> for your
            tax filings and compliance with all federal, state, and local laws.
          </p>
          <p className="text-xs text-slate-500 italic">
            Federal references in this app cite FNS Instruction 796-2 Rev. 4 (CACFP Financial Management),
            IRS Publication 587 (Business Use of Your Home), and IRS Form 8882 (Employer-Provided Child Care
            Credit). State credit programs change frequently — always verify with your state revenue
            department.
          </p>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer" data-testid="disclaimer-agree-row">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="disclaimer-checkbox"
              className="mt-1 w-4 h-4 accent-purple-600"
            />
            <span className="text-sm text-slate-700">
              I have read and understand this disclaimer. I will consult a tax professional for filings.
            </span>
          </label>
          <button
            onClick={handleAccept}
            disabled={!agreed || busy}
            data-testid="disclaimer-accept-btn"
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
          >
            {busy ? "Saving…" : (<><Check className="w-4 h-4" /> I Understand — Continue</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
